/**
 * ReopenCardDialogContainer — binds one press handler per working stage and
 * resolves the submit gate for `ReopenCardDialogView` (SE-5496).
 *
 * Both used to live in the View: a `useMemo` building the handler record, and a
 * `canSubmit` derivation after an early return. A View is a pure function of its
 * props, so the binding and the gate belong here (SE-8019).
 * @module features/player-kanban/components/molecules/ReopenCardDialog/ReopenCardDialogContainer
 */

import { useMemo } from "react";

import ReopenCardDialogView, {
  type ReopenStageOption,
} from "./ReopenCardDialogView";

/** Props for {@link ReopenCardDialogContainer}. */
export interface ReopenCardDialogContainerProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Player name for the dialog copy, when known. */
  readonly playerName?: string;
  /** Working stages the card may be reopened into. */
  readonly workingStages: readonly ReopenStageOption[];
  /** Currently-selected working stage id, if any. */
  readonly selectedStageId?: string;
  /** Stage selection handler. */
  readonly onSelectStage: (stageId: string) => void;
  /** Current optional rationale text. */
  readonly rationale: string;
  /** Rationale change handler. */
  readonly onChangeRationale: (value: string) => void;
  /** Whether a reopen is in flight. */
  readonly isSubmitting: boolean;
  /** Confirm handler. */
  readonly onSubmit: () => void;
  /** Cancel handler. */
  readonly onCancel: () => void;
}

/**
 * Resolve the per-stage handlers and the submit gate, then render the dialog.
 * @param props - {@link ReopenCardDialogContainerProps}
 * @param props.isOpen - Whether the dialog is visible
 * @param props.playerName - Player name for the copy
 * @param props.workingStages - Working stages to choose from
 * @param props.selectedStageId - Currently-selected stage id
 * @param props.onSelectStage - Stage selection handler
 * @param props.rationale - Current optional rationale text
 * @param props.onChangeRationale - Rationale change handler
 * @param props.isSubmitting - Whether a reopen is in flight
 * @param props.onSubmit - Confirm handler
 * @param props.onCancel - Cancel handler
 * @returns The bound dialog.
 */
function ReopenCardDialogContainer({
  isOpen,
  playerName,
  workingStages,
  selectedStageId,
  onSelectStage,
  rationale,
  onChangeRationale,
  isSubmitting,
  onSubmit,
  onCancel,
}: ReopenCardDialogContainerProps) {
  // Pre-bind a stable press handler per stage so the View passes a plain
  // reference (react-perf/jsx-no-new-function-as-prop forbids inline closures).
  const stageHandlers = useMemo(
    () =>
      Object.fromEntries(
        workingStages.map(stage => [stage.id, () => onSelectStage(stage.id)])
      ) as Record<string, () => void>,
    [workingStages, onSelectStage]
  );

  // A working stage MUST be chosen; the rationale stays optional.
  const canSubmit = Boolean(selectedStageId) && !isSubmitting;

  return (
    <ReopenCardDialogView
      isOpen={isOpen}
      playerName={playerName}
      workingStages={workingStages}
      selectedStageId={selectedStageId}
      stageHandlers={stageHandlers}
      rationale={rationale}
      onChangeRationale={onChangeRationale}
      isSubmitting={isSubmitting}
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}

export default ReopenCardDialogContainer;
